// One published DOCUMENT, framed.
//
// A document is not a dashboard: it is a self-contained page whose author fixed
// its layout, its controls and its arithmetic. The renderer's job here is to get
// out of the way — so it is an <iframe> rather than injected markup. Three
// reasons, in order of how much they would hurt:
//
//   1. The document ships its own <html>, <style> and <script>. Inlining it
//      would put its rules in the app's cascade and the app's rules in its
//      layout, and the whole point of serving the document is that it looks
//      exactly like itself.
//   2. Its scripts run in their own document rather than in the app's global
//      scope, so it cannot collide with (or reach into) the SPA.
//   3. The src is the same-origin authorized endpoint, so the frame's request
//      carries the `bi_authority` cookie and is authorized exactly like every
//      other call. There is no second auth path to keep in step.
import { useQuery } from "@tanstack/react-query";

import { ErrorState, LoadingState } from "../components/states";
import { BI_API_BASE } from "../config";
import { artifactsQuery } from "../lib/queries";

export function DocumentPage({ slug }: { slug: string }) {
  const artifacts = useQuery(artifactsQuery);

  if (artifacts.isPending) return <LoadingState label="Loading document…" />;
  if (artifacts.isError) {
    return <ErrorState error={artifacts.error} onRetry={() => void artifacts.refetch()} />;
  }

  const doc = artifacts.data.find((a) => a.slug === slug);
  if (!doc) {
    return (
      <ErrorState
        error={new Error(`No document called "${slug}".`)}
        onRetry={() => void artifacts.refetch()}
      />
    );
  }

  return (
    <div className="bi-page bi-page--document">
      <div className="bi-page__head">
        <h1 className="bi-page__title">{doc.title}</h1>
        <p className="bi-page__sub">{doc.description}</p>
      </div>
      <iframe
        className="bi-document__frame"
        // BI_API_BASE, not a bare "/api/..." path. It is empty in every cloud
        // env (same-origin behind one load balancer) and a different ORIGIN in
        // local dev — where a relative path is served by the dev server, which
        // answers unknown paths with index.html, so the frame silently loaded
        // the SPA inside itself instead of the document.
        src={`${BI_API_BASE}/api/bi/artifacts/${encodeURIComponent(slug)}`}
        title={doc.title}
      />
    </div>
  );
}
